<template>
  <div class="article-page-container">
    <!-- Article Page -->
    <!-- Breadcrumb Navigation -->
    <Breadcrumbs :breadcrumbs="breadcrumbs" />

    <div v-if="error" class="error-message">
      <s-icon icon-font="s-icon-warning" circle />
      Error: {{ error }}
    </div>

    <article v-else-if="article" class="article-content">
      <!-- Article Header -->
      <header class="article-header">
        <h1>{{ article?.title }}</h1>

        <!-- Author Info -->
        <div class="article-author" v-if="article?.author">
          <div class="author-avatar">
            <s-icon icon-font="s-icon-user" circle />
          </div>
          <div class="author-details">
            <span class="author-name">{{ article?.author.firstName }} {{ article?.author.lastName }}</span>
            <div class="article-meta">
              <span class="read-time">{{ calculateReadTime(article?.contentText) }} min read</span>
              <span class="separator">•</span>
              <span class="publish-date">{{ formatDate(article?.date) }}</span>
              <span class="separator">•</span>
              <span class="category s-badge s-badge-dark s-badge-no-margin"><a :href="`/pulse/categories/${article.categorySlug}`" class="">{{ article.category }}</a></span>

            </div>
          </div>
        </div>
      </header>

      <!-- Featured Image -->
      <div class="article-featured-image" v-if="article?.featuredImage || (article?.image && !article.image.startsWith('data:'))">
        <img :src="article.featuredImage || article.featuredImageSizes['background']?.url || article.image || article.imageSizes['regular']?.url" :alt="article.title">
      </div>

      <!-- Article Body -->
      <div class="article-body">
        <ArticleContent :content="article?.contentHtml" />

        <div class="article-meta-bottom">
          <BlogTags :tags="article?.tags" />
          <span class="date s-margin-l-auto">{{ formatDate(article?.date) }}</span>
        </div>
      </div>

      <!-- Article Footer -->
      <footer class="article-footer">

        <div class="article-share">
          <h3>Share this article:</h3>
          <div class="share-buttons">
            <s-button @click="shareArticle('twitter')" class="share-button twitter s-button-hover-rise">
              <s-icon icon-font="s-icon-twitter" circle />
              Tweet
            </s-button>
            <s-button @click="shareArticle('linkedin')" class="share-button linkedin s-button-hover-rise">
              <s-icon icon-font="s-icon-linkedin" circle />
              Share
            </s-button>
            <s-button @click="shareArticle('facebook')" class="share-button facebook s-button-hover-rise">
              <s-icon icon-font="s-icon-facebook" circle />
              Post
            </s-button>
            <s-button @click="copyLink" class="share-button copy-link s-button-hover-rise">
              <s-icon icon-font="s-icon-link" circle />
              Copy Link
            </s-button>
          </div>
        </div>
      </footer>


      <!-- Related Articles -->
      <section class="related-articles" v-if="relatedArticles.length">
        <h2>Related Articles</h2>
        <div class="article-grid article-flex">
          <article
            v-for="relatedArticle in relatedArticles"
            :key="relatedArticle.id"
            class="article-card"
          >
            <a :href="`/pulse/articles/${relatedArticle.id}-${relatedArticle.slug}`" class="article-image-link">
              <img
                :src="relatedArticle.imageSizes['thumbnail']?.url ?? relatedArticle.image"
                :alt="relatedArticle.title"
                class="article-image"
              >
            </a>

            <div class="article-content">
              <div class="article-meta">
                <span class="date">{{ formatDate(relatedArticle.date) }}</span>
              </div>

              <h2 class="article-title">
                <a :href="`/pulse/articles/${relatedArticle.id}-${relatedArticle.slug}`">
                  {{ relatedArticle.title }}
                </a>
              </h2>

              <p class="article-description">{{ relatedArticle.preview }}</p>

              <div class="article-tags" v-if="relatedArticle.tags?.length">
                <a
                  v-for="tag in relatedArticle.tags"
                  :key="tag.slug"
                  :href="`/pulse/tags/${tag.slug}`"
                  class="tag s-badge s-badge-blog-tag"
                  >
                  #{{ tag.name }}
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>
      <BlogQuickNav/>

    </article>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useData } from 'vike-vue/useData'
import SIcon from '/components/SIcon.vue'
import SButton from '/components/SButton.vue'
import BlogTags from '/components/BlogTags.vue'
import ArticleContent from '/components/ArticleContent.vue'
import Breadcrumbs from '/components/Breadcrumbs.vue'
import BlogQuickNav from "#root/components/BlogQuickNav.vue";

const { article, relatedArticles, error } = useData()

const breadcrumbs = computed(() => {
  const crumbs = [
    {
      name: 'Home',
      path: '/pulse'
    }
  ]

  if (article?.category) {
    crumbs.push({
      name: article.category,
      path: `/pulse/categories/${article.categorySlug}`
    })
  }

  if (article?.title) {
    crumbs.push({
      name: article.title
    })
  }

  return crumbs
})

// Format date using Intl
const formatDate = (dateString) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date(dateString))
}

// Calculate read time
const calculateReadTime = (text) => {
  const wordsPerMinute = 200
  const wordCount = text?.split(/\s+/)?.length || 0
  return Math.ceil(wordCount / wordsPerMinute)
}

// Social sharing functions
const shareArticle = (platform) => {
  const url = encodeURIComponent(window.location.href)
  const title = encodeURIComponent(article?.title)

  const shareUrls = {
    twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`
  }

  window.open(shareUrls[platform], '_blank')
}

// Copy link to clipboard
const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href)

    // toast notification for success goes here
  } catch (err) {
    console.error('Failed to copy link:', err)
  }
}
</script>

